import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { PaginationArrow } from '@/common';
import { Search } from '@/components/common/Search';
import { IconButton, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { FilterPopUpValues } from '@/components/layouts';
import { PAGE_SIZE } from '@/constants';
import { ContentSetup } from '@/pages/DigitalSignage/DeviceManagement//ContentSetup';
import { ScreenCalendar } from '@/pages/DigitalSignage/DeviceManagement/ScreenCalendar';
import { ScreenCard } from '@/pages/DigitalSignage/DeviceManagement/ScreenCard';
import { FilterIcon } from '@/resources';
import { useGetAllScreens } from '@/services';
import { useGetDeviceScheduleByIdQuery } from '@/services/deviceSchedule.service';
import { TestIds } from '@/shared/test-ids';
import { SelectOptionProps } from '@/types';

import { FilterScreens } from './FilterScreens';

export const DigitalSignageDevice = () => {
  const [t] = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>();
  const { ref: inViewRef, inView } = useInView();
  const [selectedScreenId, setSelectedScreenId] = useState<number | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const hasAppliedReturnContextRef = useRef(false);
  const [screenCalendarRefetch, setScreenCalendarRefetch] = useState<(() => void) | undefined>(
    undefined
  );
  const [isOpenFilterModal, setIsOpenFilterModal] = useState<boolean>(false);
  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);

  const [filters, setFilters] = useState<{
    device: SingleValue<SelectOptionProps>;
  }>({
    device: null
  });

  const {
    isLoading,
    data: allScreens,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGetAllScreens(PAGE_SIZE, search, filters.device?.value as number);

  const returnDeviceScheduleId = Number(searchParams.get('deviceScheduleId') ?? 0);
  const { data: returnDeviceSchedule } = useGetDeviceScheduleByIdQuery(returnDeviceScheduleId);

  useEffect(() => {
    if (hasAppliedReturnContextRef.current || !returnDeviceSchedule?.data) {
      return;
    }

    setSelectedScreenId(returnDeviceSchedule.data.screenId);
    setSelectedEventId(String(returnDeviceSchedule.data.id));
    hasAppliedReturnContextRef.current = true;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('deviceScheduleId');
      next.delete('returnTo');
      return next;
    });
  }, [returnDeviceSchedule, setSearchParams]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage]);

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage]);

  useEffect(() => {
    const numberOfFilters = Object.values(filters).filter((value) => value !== null);
    setAppliedFiltersCounter(numberOfFilters.length);
  }, [filters]);

  const handleOpenFilterModal = () => {
    setIsOpenFilterModal(!isOpenFilterModal);
  };

  const resetFilters = () => {
    setIsOpenFilterModal(false);
    setFilters({ device: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: { filterByDevice: SelectOptionProps }) => {
    setIsOpenFilterModal(false);
    setFilters({
      device: values.filterByDevice
    });
    setSelectedScreenId(null);
    setSelectedEventId(null);
  };

  return (
    <div data-testid={TestIds.digitalSignage.page} className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-row pl-3'>
          <div className='flex w-2/6 flex-col border-r-2 border-primary-gray md:px-[20px] md:py-5'>
            <div className='sticky right-0 top-0 z-20 flex w-full'>
              <Label text={t('screens')} type={LABEL_TYPE.H1} className='left-0 my-3 md:mt-0' />
            </div>

            <div className='my-2 flex w-full flex-row justify-between'>
              <Search callback={searchCallback} testId={TestIds.digitalSignage.deviceSearchInput} />
              <div className='relative ml-4'>
                <div className='relative size-10'>
                  <IconButton
                    dataTestId={TestIds.digitalSignage.deviceFilterButton}
                    className='size-full'
                    variant={IconButtonVariant.PRIMARY}
                    handleOnClick={handleOpenFilterModal}>
                    <FilterIcon className='size-5' />
                  </IconButton>
                  {appliedFiltersCounter > 0 && (
                    <div className='absolute right-0 top-1 flex size-5 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-primary-blue text-primary-white'>
                      <Label text={appliedFiltersCounter} type={LABEL_TYPE.PARAGRAPH2} />
                    </div>
                  )}
                </div>

                {isOpenFilterModal && (
                  <div className='absolute left-0 top-full z-50 mt-2'>
                    <FilterScreens
                      apply={applyFilters as (values: FilterPopUpValues) => void}
                      reset={resetFilters}
                      onClose={() => setIsOpenFilterModal(false)}
                      filterByDevice={filters.device}
                    />
                  </div>
                )}
              </div>
            </div>

            <div
              data-testid={TestIds.digitalSignage.screensList}
              className='no-scrollbar mt-3 flex flex-col gap-y-2 overflow-auto'
              style={{ maxHeight: 'calc(100vh - 120px)' }}>
              {allScreens?.pages
                ?.flatMap((page) => page.data?.items)
                .map((screen) => (
                  <ScreenCard
                    key={screen.id}
                    name={screen.name}
                    deviceName={screen.device.name}
                    isSelected={selectedScreenId === screen.id}
                    onClick={() =>
                      selectedScreenId === screen.id
                        ? (setSelectedScreenId(null), setSelectedEventId(null))
                        : (setSelectedScreenId(screen.id), setSelectedEventId(null))
                    }
                  />
                ))}
              <div ref={inViewRef}></div>
            </div>

            <PaginationArrow isFetchingNextPage={isFetchingNextPage} inView={inView} />
          </div>
          {selectedScreenId && (
            <div
              data-testid={TestIds.digitalSignage.calendarPanel}
              className='flex w-1/4 flex-col border-r-2 border-primary-gray'>
              <ScreenCalendar
                screenId={selectedScreenId}
                selectedEventId={selectedEventId}
                onEventSelect={setSelectedEventId}
                setRefetch={setScreenCalendarRefetch}
              />
            </div>
          )}
          {selectedEventId && (
            <div data-testid={TestIds.digitalSignage.contentSetupPanel} className='flex-1'>
              <ContentSetup
                deviceScheduleId={Number(selectedEventId)}
                onEventSelect={setSelectedEventId}
                refetchDeviceSchedules={screenCalendarRefetch}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
